// Local, privacy-first analytics stored entirely in the visitor's browser.
// No data leaves the device. Replaces the former Supabase-backed analytics.

const STORAGE_KEY = 'nide_local_analytics_v1';
const MAX_EVENTS = 500; // cap stored events to keep localStorage small

export interface LocalAnalyticsEvent {
  type: 'session' | 'page_view' | 'artwork_view' | 'song_play';
  timestamp: number;
  // session
  deviceType?: string;
  browser?: string;
  operatingSystem?: string;
  country?: string;
  // page_view
  pagePath?: string;
  // artwork_view
  artworkName?: string;
  viewDuration?: number;
  // song_play
  songTitle?: string;
  songArtist?: string;
  playDuration?: number;
  completed?: boolean;
}

interface LocalAnalyticsStore {
  events: LocalAnalyticsEvent[];
}

const readStore = (): LocalAnalyticsStore => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed.events)) return parsed;
    }
  } catch {
    // ignore corrupted storage
  }
  return { events: [] };
};

const writeStore = (store: LocalAnalyticsStore) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // storage full or unavailable — analytics must never break the app
  }
};

export const recordEvent = (event: Omit<LocalAnalyticsEvent, 'timestamp'>) => {
  const store = readStore();
  store.events.push({ ...event, timestamp: Date.now() });
  if (store.events.length > MAX_EVENTS) {
    store.events = store.events.slice(-MAX_EVENTS);
  }
  writeStore(store);
};

export const getEvents = (): LocalAnalyticsEvent[] => readStore().events;

const countBy = (events: LocalAnalyticsEvent[], key: (e: LocalAnalyticsEvent) => string | undefined) => {
  const map = new Map<string, number>();
  events.forEach(e => {
    const k = key(e);
    if (k) map.set(k, (map.get(k) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
};

export const getLocalSummary = () => {
  const events = getEvents();
  const sessions = events.filter(e => e.type === 'session');
  const pageViews = events.filter(e => e.type === 'page_view');
  const artworkViews = events.filter(e => e.type === 'artwork_view');
  const songPlays = events.filter(e => e.type === 'song_play');

  const timestamps = events.map(e => e.timestamp);
  const first = timestamps.length ? new Date(Math.min(...timestamps)).toLocaleDateString() : '—';
  const last = timestamps.length ? new Date(Math.max(...timestamps)).toLocaleDateString() : '—';

  return {
    totalSessions: sessions.length,
    totalPageViews: pageViews.length,
    totalArtworkViews: artworkViews.length,
    totalSongPlays: songPlays.length,
    activePeriod: `${first} – ${last}`,
    topCountries: countBy(events, e => e.country),
    topDevices: countBy(events, e => e.deviceType),
    topBrowsers: countBy(events, e => e.browser),
  };
};

export interface LocalSongStat {
  songTitle: string;
  songArtist: string;
  playCount: number;
  totalPlayTime: number;
  completionRate: number;
}

export const getLocalSongStats = (): LocalSongStat[] => {
  const plays = getEvents().filter(e => e.type === 'song_play');
  const map = new Map<string, LocalSongStat & { completed: number }>();

  plays.forEach(p => {
    const key = `${p.songTitle}|${p.songArtist}`;
    const existing = map.get(key) || {
      songTitle: p.songTitle || 'Unknown',
      songArtist: p.songArtist || 'Unknown',
      playCount: 0,
      totalPlayTime: 0,
      completionRate: 0,
      completed: 0,
    };
    existing.playCount += 1;
    existing.totalPlayTime += p.playDuration || 0;
    if (p.completed) existing.completed += 1;
    map.set(key, existing);
  });

  return Array.from(map.values())
    .map(s => ({ ...s, completionRate: s.playCount ? Math.round((s.completed / s.playCount) * 100) : 0 }))
    .sort((a, b) => b.totalPlayTime - a.totalPlayTime);
};

export interface LocalArtworkStat {
  artworkName: string;
  totalViews: number;
  totalDuration: number;
  avgDuration: number;
}

export const getLocalArtworkStats = (): LocalArtworkStat[] => {
  const views = getEvents().filter(e => e.type === 'artwork_view');
  const map = new Map<string, { totalViews: number; totalDuration: number }>();

  views.forEach(v => {
    const key = v.artworkName || 'Unknown';
    const existing = map.get(key) || { totalViews: 0, totalDuration: 0 };
    existing.totalViews += 1;
    existing.totalDuration += v.viewDuration || 0;
    map.set(key, existing);
  });

  return Array.from(map.entries())
    .map(([artworkName, s]) => ({
      artworkName,
      totalViews: s.totalViews,
      totalDuration: s.totalDuration,
      avgDuration: s.totalViews ? Math.round(s.totalDuration / s.totalViews) : 0,
    }))
    .sort((a, b) => b.totalViews - a.totalViews);
};
